// A small markdown reader for Fathom's notes — T70 (14 Sep 2026).
//
// Fathom writes a meeting's summary as markdown: a heading or two, bullet lists, the odd bold
// run, a link. That is the whole of what this reads — headings, bullets, numbered lists,
// paragraphs, bold and links — into blocks a component draws (ui/Markdown.tsx). No HTML is ever
// interpreted: the text came from an outside system and is drawn as text, which is also why
// this is sixty lines and not a dependency.
//
// T176 (30 Sep 2026): kadmon's answers in the brain's chat too. She writes light markdown (a bold
// name, a numbered list, a LinkedIn whole on the line under a person), so a bare http(s) address
// is a link, a line straight under a list item belongs to that item, and her chat keeps each line
// a line (`keepLines`), where Fathom's wrapped paragraphs are joined.

export type Inline =
  | { kind: 'text'; text: string }
  | { kind: 'bold'; text: string }
  | { kind: 'link'; text: string; href: string }
  | { kind: 'break' };

export type Block =
  | { kind: 'heading'; level: 1 | 2 | 3; inline: Inline[] }
  | { kind: 'paragraph'; inline: Inline[] }
  | { kind: 'list'; ordered: boolean; items: Inline[][] };

const LINK = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/;
const BOLD = /\*\*([^*]+)\*\*/;
/** A bare address, http(s) only; a closing bracket or the sentence's own full stop is not part of it. */
const URL = /https?:\/\/[^\s<>()]*[^\s<>().,;:!?'"]/;

/** Bold runs and links out of one line; everything else is text. */
export function inlineOf(line: string): Inline[] {
  const out: Inline[] = [];
  let rest = line;
  while (rest.length > 0) {
    const link = LINK.exec(rest);
    const bold = BOLD.exec(rest);
    const url = URL.exec(rest);
    const first = [link, bold, url]
      .filter((m): m is RegExpExecArray => m !== null)
      .sort((a, b) => a.index - b.index)[0];
    if (!first) {
      out.push({ kind: 'text', text: rest });
      break;
    }
    if (first.index > 0) out.push({ kind: 'text', text: rest.slice(0, first.index) });
    if (first === link) out.push({ kind: 'link', text: first[1]!, href: first[2]! });
    else if (first === url) out.push({ kind: 'link', text: first[0], href: first[0] });
    else out.push({ kind: 'bold', text: first[1]! });
    rest = rest.slice(first.index + first[0].length);
  }
  return out;
}

/** Lines as one run of inlines, each line after the first on a line of its own. */
const onLines = (lines: readonly string[]): Inline[] =>
  lines.flatMap((line, i) =>
    i === 0 ? inlineOf(line) : [{ kind: 'break' as const }, ...inlineOf(line)],
  );

/**
 * The notes as blocks: a heading, a list, or a paragraph of the lines between blank ones. With
 * `keepLines` (T176, kadmon's answers), a paragraph's lines stay lines.
 */
export function parseMarkdown(markdown: string, keepLines = false): Block[] {
  const blocks: Block[] = [];
  let paragraph: string[] = [];
  // T176: whether the line before this one was a list item, so a line straight under it is its own.
  let inItem = false;
  // T488g: how far in the open item's line starts; a line indented past it is that item's own.
  let itemIndent = 0;
  const flush = () => {
    if (paragraph.length > 0) {
      blocks.push({
        kind: 'paragraph',
        inline: keepLines ? onLines(paragraph) : inlineOf(paragraph.join(' ')),
      });
      paragraph = [];
    }
  };
  for (const raw of markdown.replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.trimEnd();
    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    const bullet = /^\s*[-*+]\s+(.*)$/.exec(line);
    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    const last = blocks.at(-1);
    if (line.trim() === '') {
      flush();
      inItem = false;
    } else if (inItem && !heading && !bullet && !numbered && last?.kind === 'list') {
      last.items.at(-1)!.push({ kind: 'break' }, ...inlineOf(line.trim()));
    } else if (
      inItem &&
      (bullet || numbered) &&
      /^\s*/.exec(raw)![0].length > itemIndent &&
      last?.kind === 'list'
    ) {
      // T488g: an indented item under one (a contact under a person) stays inside that item.
      last.items.at(-1)!.push({ kind: 'break' }, ...inlineOf((bullet ?? numbered)![1]!));
    } else if (heading) {
      flush();
      inItem = false;
      blocks.push({
        kind: 'heading',
        level: heading[1]!.length as 1 | 2 | 3,
        inline: inlineOf(heading[2]!),
      });
    } else if (bullet || numbered) {
      flush();
      const ordered = numbered !== null && bullet === null;
      const text = (bullet ?? numbered)![1]!;
      const list = blocks.at(-1);
      if (list && list.kind === 'list' && list.ordered === ordered) list.items.push(inlineOf(text));
      else blocks.push({ kind: 'list', ordered, items: [inlineOf(text)] });
      inItem = true;
      itemIndent = /^\s*/.exec(raw)![0].length;
    } else {
      paragraph.push(line.trim());
    }
  }
  flush();
  return blocks;
}
